import { useState, type JSX } from 'react';

import { useQuery } from '@tanstack/react-query';

import { qualityProjectsQueryOptions } from '@/lib/api/quality.js';
import { ConnectorIcon } from '@/integration/components/connectors/ConnectorIcon.js';
import { cn } from '@/lib/utils.js';
import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';

import {
  QUALITY_CONNECTOR_NAMES,
  useConnectorBadgeStatus,
  type QualityConnectorId,
} from './connector-status.js';
import { ConnectorPrompt } from './ConnectorPrompt.js';

const stateWords = {
  connected: 'connected',
  disconnected: 'not connected',
  error: 'needs reconnect',
  unavailable: 'status unavailable',
} as const;

/**
 * A 15 px connector tile beside a Quality source: brand colour when connected, grey when not, and a
 * red dot when the connection needs a reconnect. It is never disabled — a click always opens the
 * modal, which is where connecting, reconnecting or asking an admin happens.
 *
 * The badge reads the workspace of the project it belongs to: `workspaceId` when the caller knows it,
 * else the workspace the projects list gives `projectId`. It never falls back to the selected
 * workspace, because a suite, a run or a settings page can belong to a different one.
 *
 * Its clicks and keys stop at the badge, and so do those inside its modal: the modal portals out of
 * the DOM but not out of the React tree, so without the stop they would toggle a source row the badge
 * sits in.
 */
export function ConnectorBadge({
  provider,
  projectId,
  workspaceId,
  className,
  label,
  choices,
  statusOverride,
  onManagementClose,
  onChoiceChange,
  explainUnavailableActions = false,
}: {
  provider: QualityConnectorId;
  projectId: string | undefined;
  workspaceId?: string | null;
  className?: string;
  label?: string;
  choices?: readonly QualityConnectorId[];
  statusOverride?: {
    state: 'connected' | 'disconnected' | 'unavailable';
    source: 'identity' | 'project' | null;
    reconnectConnectionId: null;
  };
  onManagementClose?: () => void;
  /** Called after an owner choice is saved or cleared in the open modal. */
  onChoiceChange?: () => void;
  explainUnavailableActions?: boolean;
}): JSX.Element {
  const [selectedProvider, setSelectedProvider] = useState<
    QualityConnectorId | undefined
  >();
  const [choosing, setChoosing] = useState(false);
  const effectiveProvider = selectedProvider ?? provider;
  const projectsQuery = useQuery({
    ...qualityProjectsQueryOptions(),
    enabled: workspaceId === undefined && Boolean(projectId),
  });
  const effectiveWorkspaceId =
    workspaceId !== undefined
      ? workspaceId
      : (projectsQuery.data?.find((project) => project.id === projectId)
          ?.workspace_id ?? null);
  const observedStatus = useConnectorBadgeStatus(
    effectiveProvider,
    projectId,
    effectiveWorkspaceId,
  );
  const status = statusOverride ?? observedStatus;
  const [open, setOpen] = useState(false);
  const name = QUALITY_CONNECTOR_NAMES[effectiveProvider];
  const hint = choices?.length
    ? 'Choose GitHub or GitLab, connect it, then select repositories for this project.'
    : status.state === 'connected'
      ? `${name} is connected. Open its settings to configure this project's source.`
      : `Connect ${name}, then configure this project's source.`;
  const Trigger = label ? Button : 'button';
  return (
    <>
      <Trigger
        {...(label
          ? { variant: 'brand-outline' as const, size: 'sm' as const }
          : {})}
        type="button"
        data-provider={effectiveProvider}
        data-state={status.state}
        aria-label={
          label
            ? `${label}: ${choices?.length ? 'Git' : name} setup`
            : `${name}: ${stateWords[status.state]}`
        }
        title={label ? hint : `${name}: ${stateWords[status.state]}`}
        onClick={(event) => {
          event.stopPropagation();
          if (choices?.length) setChoosing(true);
          else setOpen(true);
        }}
        onKeyDown={(event) => event.stopPropagation()}
        className={cn(
          label
            ? 'relative !rounded-md'
            : 'relative inline-grid size-[15px] shrink-0 place-items-center rounded-[4px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          className,
        )}
      >
        {label ?? (
          <ConnectorIcon
            id={effectiveProvider}
            className={cn(
              'size-[15px] rounded-[4px]',
              status.state !== 'connected' && 'opacity-40 grayscale',
            )}
          />
        )}
        {status.state === 'error' ? (
          <span
            aria-hidden
            className="absolute -top-[3px] -right-[3px] size-[7px] rounded-full border-[1.5px] border-background bg-red-600"
          />
        ) : null}
      </Trigger>
      {open ? (
        <ConnectorPrompt
          explainUnavailableActions={explainUnavailableActions}
          provider={effectiveProvider}
          projectId={projectId}
          statusOverride={statusOverride}
          onChoiceChange={onChoiceChange}
          workspaceId={effectiveWorkspaceId}
          onClose={() => {
            setOpen(false);
            onManagementClose?.();
          }}
        />
      ) : null}
      {choosing ? (
        <span
          className="contents"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <AppDialog
            open={choosing}
            onOpenChange={setChoosing}
            title="Choose a Git connector"
            description="Connect a provider, then choose its repositories for this project."
          >
            <div className="flex gap-3">
              {choices?.map((choice) => (
                <Button
                  key={choice}
                  onClick={() => {
                    setSelectedProvider(choice);
                    setChoosing(false);
                    setOpen(true);
                  }}
                >
                  <ConnectorIcon id={choice} className="size-4" />
                  {QUALITY_CONNECTOR_NAMES[choice]}
                </Button>
              ))}
            </div>
          </AppDialog>
        </span>
      ) : null}
    </>
  );
}
